import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ResponsiveSankey } from '@nivo/sankey';
import { Activity, Bot, Wrench, Brain, Search, Plug, Server, PanelRight, ArrowRight, CheckCircle, Layers, ArrowDownUp, Workflow } from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSessionJwt } from '../hooks/useSessionJwt';
import { useTheme } from '../contexts/ThemeContext';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useTimeRange } from '../contexts/TimeRangeContext';
import { v2ServicePath } from '../config';
import { palette } from '../utils/designTokens';
import { getOutcomeLabel } from '../utils/outcomeUtils';
import { compareNodesByCategory } from '../utils/journeyNodeOrder';
import { getColorFromTypeFamily } from '../utils/journeyNodeColor';
import { traceFilterKey, isNodeClickable, isInertNodeRect } from '../utils/journeyDrilldown';
import SlideOutDrawer from './SlideOutDrawer';
import SystemPromptPanel from './SystemPromptPanel';

// Type hue ranges for color family grouping

// Type icons (lucide-react components)
const TYPE_ICON_COMPONENTS = {
  AGENT: Bot,
  TOOL: Wrench,
  MODEL: Brain,
  CHAIN: Workflow,
  LLM: Brain,
  OPERATION: Activity,
  RETRIEVER: Search,
  RERANKER: ArrowDownUp,
  EMBEDDING: Layers,
  MCP: Plug,
  SERVICE: Server,
  WORKFLOW: ArrowRight,  // Business task grouping (entry point)
  OUTCOME: CheckCircle,  // Trace outcome (exit point)
};

const NODE_TYPE_DISPLAY_LABELS = {
  WORKFLOW: 'Business Task',
  // A step the framework did not type as an agent. CHAIN is OpenInference's
  // word, not something to put in front of a customer.
  CHAIN: 'Step',
  LLM: 'Model',
};

const getNodeTypeLabel = (nodeType) => NODE_TYPE_DISPLAY_LABELS[nodeType] || nodeType;

// Call-layer span kinds the chart leaves out. Empty: every kind of call is drawn.
//
// Filter by span kind only, never by name. A tool is whatever the
// instrumentation reported, and excluding one by name would make the chart
// right for the framework that emits that name and silently wrong for the rest.
const HIDDEN_CALL_TYPES = new Set();


export default function JourneySankey({ data, loading, error, topMargin = 40 }) {
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS

  // Get theme from context (includes isDark boolean)
  const { isDark } = useTheme();

  // Navigation and context hooks
  const navigate = useNavigate();
  // Nodes whose band leads nowhere, by layout position. The container only sees
  // the <rect> under the pointer, so position is what identifies it.
  const inertNodeBoxes = useRef([]);
  const location = useLocation();
  const { selectedAppId } = useParams();
  const { selectedEnvironment } = useEnvironment();
  const { selectedRange } = useTimeRange();
  const { sessionId, getJwt } = useSessionJwt();

  // Track drawer state for node details
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);

  // Cache for fetched data (agent prompts, tool definitions)
  const [dataCache, setDataCache] = useState({});

  // Track tool definitions prefetch status
  const [toolDefsStatus, setToolDefsStatus] = useState({
    state: 'idle',  // 'idle' | 'loading' | 'success' | 'error'
    error: null
  });

  // Track which parameter sets we've attempted to prefetch (prevents duplicate fetches)
  const prefetchAttemptedRef = useRef(new Set());

  // Track label color reactively to avoid animation errors
  const [labelColor, setLabelColor] = useState(() => {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--color-text-primary')
      .trim() || '#F4F4F5';
  });

  useEffect(() => {
    // Listen for theme changes (CSS custom properties only)
    const updateLabelColor = () => {
      const newLabelColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-text-primary')
        .trim() || '#F4F4F5';
      setLabelColor(newLabelColor);
    };

    const observer = new MutationObserver(updateLabelColor);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    return () => observer.disconnect();
  }, []);

  // Prefetch tool definitions after Sankey data loads
  useEffect(() => {
    // Only prefetch if:
    // - We have Sankey data
    // - We haven't started fetching yet
    // - We have the required context
    const jwt = getJwt();
    if (!data || !selectedAppId || !selectedEnvironment || !selectedRange || !jwt) {
      return;
    }

    // Create a unique key for this set of parameters
    const prefetchKey = `${selectedAppId}-${selectedEnvironment}-${selectedRange}`;

    // Skip if we've already successfully prefetched for these params
    if (prefetchAttemptedRef.current.has(prefetchKey)) {
      return;
    }

    // Start background fetch
    setToolDefsStatus({ state: 'loading', error: null });

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const endpoint = v2ServicePath(selectedAppId, '/tools/definitions');
    const url = `${apiUrl}${endpoint}?env=${encodeURIComponent(selectedEnvironment)}&range=${encodeURIComponent(selectedRange)}`;

    const controller = new AbortController();

    fetch(url, {
      headers: { Authorization: `Bearer ${jwt}` },
      signal: controller.signal,
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        const allDefinitions = data.data || [];
        setDataCache(prev => ({
          ...prev,
          '__all_tool_definitions__': allDefinitions
        }));
        setToolDefsStatus({ state: 'success', error: null });
        prefetchAttemptedRef.current.add(prefetchKey);
      })
      .catch(err => {
        if (err.name === 'AbortError') return;
        setToolDefsStatus({ state: 'error', error: err.message });
      });

    return () => controller.abort();
  }, [data, selectedAppId, selectedEnvironment, selectedRange, sessionId, getJwt, setDataCache]);

  // Assign colors to nodes based on type family + name hash.
  //
  // No node repeats anywhere. A tool several steps call is one node with one
  // incoming edge per caller, the same way an agent reached from two places is
  // one node.
  const dataWithColors = useMemo(() => {
    if (!data || !data.nodes) return { nodes: [], links: [] };

    const callNodes = (data.call_nodes || []).filter(n => !HIDDEN_CALL_TYPES.has(n.type));
    const shown = new Set(callNodes.map(n => n.id));
    const callLinks = (data.call_links || []).filter(l => shown.has(l.target));

    // Several raw outcomes share one label — success, information and
    // acknowledged all read "Completed" — so the chart drew three right-edge
    // nodes for an outcome the rest of the product calls one thing. Merge them
    // under the label and carry every raw value in the filter, which the traces
    // page accepts comma-separated. '' is its "(none)" sentinel.
    const mergedOutcome = new Map();
    const remap = new Map();
    for (const node of data.nodes) {
      if (node.type !== 'OUTCOME') continue;
      const raw = node.filter_value ?? '';
      const label = getOutcomeLabel(raw);
      const id = `OUT: ${label}`;
      remap.set(node.id, id);
      const merged = mergedOutcome.get(id) || { ...node, id, label, trace_count: 0, raws: [] };
      merged.trace_count += node.trace_count || 0;
      merged.raws.push(raw === '' ? '(none)' : raw);
      mergedOutcome.set(id, merged);
    }
    const outcomeNodes = [...mergedOutcome.values()].map(({ raws, ...n }) => ({
      ...n, filter_value: raws.join(','),
    }));

    const flowNodes = data.nodes.filter(n => n.type !== 'OUTCOME');
    const combined = new Map();
    for (const l of data.links || []) {
      const target = remap.get(l.target) || l.target;
      const key = `${l.source}\u0000${target}`;
      const prev = combined.get(key);
      if (prev) prev.value += l.value;
      else combined.set(key, { ...l, target, value: l.value });
    }
    const flowLinks = [...combined.values()];

    return {
      ...data,
      nodes: [...flowNodes, ...outcomeNodes, ...callNodes].map(node => {
        const nodeType = node.type || 'UNKNOWN';
        const nodeColor = nodeType !== 'UNKNOWN'
          ? getColorFromTypeFamily(node.label, nodeType, isDark)
          : '#999999';

        return {
          ...node,
          displayText: node.label,
          nodeColor,
          nodeType,
        };
      }),
      links: [...flowLinks, ...callLinks],
    };
    // isDark belongs here: the node fills are computed from it, so without it a
    // theme switch keeps the previous palette until the data happens to change.
  }, [data, isDark]);

  // Calculate dynamic spacing and margins based on visible (filtered) nodes.
  const { nodeCount, calculatedHeight, leftMargin, rightMargin } = useMemo(() => {
    if (!dataWithColors.nodes || dataWithColors.nodes.length === 0) {
      return { nodeCount: 0, calculatedHeight: 300, leftMargin: 160, rightMargin: 160 };
    }
    const nodeCount = dataWithColors.nodes.length;
    const minHeight = 300;
    const heightPerNode = 50;
    const calculatedHeight = Math.max(minHeight, nodeCount * heightPerNode);

    const maxLabelLength = Math.max(...dataWithColors.nodes.map(n => n.label.length));
    const leftMargin = Math.min(250, Math.max(160, maxLabelLength * 8));
    const rightMargin = leftMargin;

    return { nodeCount, calculatedHeight, leftMargin, rightMargin };
  }, [dataWithColors]);

  // Handle node click - navigate to traces filtered by node type
  const handleNodeClick = useCallback((node) => {
    // nodeType might be in node.nodeType (from our dataWithColors) or node.type (from Nivo)
    const nodeType = node.nodeType || node.type;
    // Use displayText for filtering (clean name), not composite ID
    const nodeName = node.filter_value || node.displayText;

    const params = new URLSearchParams();

    // Preserve current context
    if (selectedEnvironment) params.set('env', selectedEnvironment);
    if (selectedRange) params.set('range', selectedRange);

    const filterKey = traceFilterKey(node);
    if (!filterKey) {
      return;
    }
    params.set(filterKey, nodeName);

    // Navigate with referrer state for breadcrumb
    navigate(`/app/${selectedAppId}/traces?${params.toString()}`, {
      state: {
        referrerUrl: location.pathname + location.search,
        selectedNode: nodeName,
        selectedNodeType: nodeType,
        fromJourney: true
      }
    });
  }, [navigate, selectedAppId, selectedEnvironment, selectedRange, location]);

  // Handle info icon click - open drawer with node details
  const handleInfoClick = useCallback((node, event) => {
    event.stopPropagation(); // Prevent node click from firing
    setSelectedNode(node);
    setDrawerOpen(true);
  }, []);

  // Extract node color (useCallback to prevent recreation)
  const getNodeColor = useCallback((node) => node.nodeColor, []);

  // Memoize inline styles to prevent recreation on every render
  const clickableLabelContainerStyle = useMemo(() => ({
    cursor: 'pointer',
    display: 'inline-flex',
    flexDirection: 'column',
    gap: '2px',
  }), []);

  const typeLabelBaseStyle = useMemo(() => ({
    fontSize: '10px',
    fontWeight: 'bold',
    color: labelColor,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  }), [labelColor]);

  const nodeIdLabelStyle = useMemo(() => ({
    fontSize: '12px',
    color: labelColor,
  }), [labelColor]);

  const textLabelContainerStyle = useMemo(() => ({
    fontSize: '12px',
    fill: labelColor,
    pointerEvents: 'none',
  }), [labelColor]);

  const panelIconStyle = useMemo(() => ({
    color: palette.interactive.panelIcon,
    flexShrink: 0
  }), []);

  // Handle link click - navigate to traces filtered by BOTH source and target
  const handleLinkClick = useCallback((link) => {
    const sourceName = link.source.filter_value || link.source.displayText;
    const targetName = link.target.filter_value || link.target.displayText;

    const params = new URLSearchParams();

    // Preserve current context
    if (selectedEnvironment) params.set('env', selectedEnvironment);
    if (selectedRange) params.set('range', selectedRange);

    // A remote endpoint has no key, so a band into another service still filters
    // on the end that is local rather than returning nothing.
    const sourceKey = traceFilterKey(link.source);
    const targetKey = traceFilterKey(link.target);

    if (!sourceKey && !targetKey) {
      return;
    }

    if (sourceKey === targetKey) {
      // Same type: use comma-separated format
      if (sourceName === targetName) {
        // Self-loop: just use single value
        params.set(sourceKey, sourceName);
      } else {
        // Different nodes, same type: comma-separated
        params.set(sourceKey, `${sourceName},${targetName}`);
      }
    } else if (sourceKey && targetKey) {
      // Different types: separate params
      params.set(sourceKey, sourceName);
      params.set(targetKey, targetName);
    } else if (sourceKey) {
      params.set(sourceKey, sourceName);
    } else if (targetKey) {
      params.set(targetKey, targetName);
    }

    navigate(`/app/${selectedAppId}/traces?${params.toString()}`, {
      state: {
        referrerUrl: location.pathname + location.search,
        selectedLink: `${sourceName} → ${targetName}`,
        fromJourney: true
      }
    });
  }, [navigate, selectedAppId, selectedEnvironment, selectedRange, location]);

  // Memoize Sankey click handler - detect links vs nodes
  const handleSankeyClick = useCallback((data, event) => {
    // Check if it's a link (has source/target)
    if (data.source && data.target) {
      handleLinkClick(data);
    }
    // Check if it's a node (has id and type)
    else if (data.id && (data.nodeType || data.type)) {
      handleNodeClick(data);
    }
  }, [handleNodeClick, handleLinkClick]);

  // Memoize Sankey margin object
  const sankeyMargin = useMemo(() => ({
    top: topMargin,
    right: rightMargin,
    bottom: 40,
    left: leftMargin
  }), [topMargin, rightMargin, leftMargin]);

  // Memoize nodeSpacing calculation
  const nodeSpacing = useMemo(() =>
    Math.max(40, Math.floor(calculatedHeight / nodeCount) - 18),
    [calculatedHeight, nodeCount]
  );

  // Memoize theme object
  const sankeyTheme = useMemo(() => ({
    background: 'transparent',
    text: {
      fill: labelColor,
      fontSize: 12,
    },
    tooltip: {
      container: {
        background: 'var(--color-panel-secondary)',
        color: 'var(--color-text-primary)',
      },
    },
  }), [labelColor]);

  // Memoize Sankey nodeBorderColor object
  const nodeBorderColor = useMemo(() => ({
    from: 'color',
    modifiers: [['darker', 0.8]]
  }), []);

  // Memoize tspan style for type labels
  const typeLabelTspanStyle = useMemo(() => ({
    fontWeight: 'bold',
    fontSize: '10px'
  }), []);

  // Custom layer to render multi-line labels - clickable for AGENT/TOOL nodes
  const CustomLabelsLayer = useCallback(({ nodes }) => {
    // Written during render on purpose: nivo computes the layout and this is the
    // only place the geometry is available. It is a ref, so nothing re-renders.
    inertNodeBoxes.current = nodes
      .filter((n) => !isNodeClickable(n))
      .map((n) => ({ x: n.x, y: n.y, w: n.width, h: n.height }));
    return (
      <g>
        {nodes.map((node) => {
          const typeLabel = node.nodeType ? `[${getNodeTypeLabel(node.nodeType)}]` : '';
          const x = node.x + node.width + 16; // labelPadding
          const y = node.y + node.height / 2;
          const isDetailsNode = node.nodeType === 'AGENT' || node.nodeType === 'TOOL';

          return (
            <g key={node.id}>
              {/* Connector line from node to label */}
              <line
                x1={node.x + node.width}
                y1={y}
                x2={x - 4}
                y2={y}
                stroke={labelColor}
                strokeWidth={1}
                strokeOpacity={0.3}
                pointerEvents="none"
              />

              {/* Clickable label for AGENT/TOOL nodes to open details drawer */}
              {isDetailsNode ? (
                <foreignObject
                  x={x}
                  y={y - 20}
                  width={250}
                  height={40}
                >
                  <div
                    style={clickableLabelContainerStyle}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleInfoClick(node, e);
                    }}
                    title="Click to view details"
                  >
                    {typeLabel && (
                      <div style={typeLabelBaseStyle}>
                        {typeLabel}
                        <PanelRight size={12} style={panelIconStyle} />
                      </div>
                    )}
                    <div style={nodeIdLabelStyle}>
                      {node.displayText}
                    </div>
                  </div>
                </foreignObject>
              ) : (
                /* Non-clickable label for LLM and other nodes */
                <text
                  x={x}
                  y={y}
                  textAnchor="start"
                  dominantBaseline="middle"
                  style={textLabelContainerStyle}
                >
                  {typeLabel && (
                    <tspan x={x} dy="0" style={typeLabelTspanStyle}>
                      {typeLabel}
                    </tspan>
                  )}
                  <tspan x={x} dy={typeLabel ? '1.2em' : '0'}>
                    {node.displayText}
                  </tspan>
                </text>
              )}
            </g>
          );
        })}
      </g>
    );
  }, [labelColor, handleInfoClick, clickableLabelContainerStyle, typeLabelBaseStyle, nodeIdLabelStyle, panelIconStyle, textLabelContainerStyle, typeLabelTspanStyle]);

  // NOW WE CAN RETURN EARLY (after all hooks are called)
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center text-text-muted text-sm">
        Loading flow data...
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="bg-danger/10 border border-danger/20 rounded-lg p-4">
          <p className="text-danger text-sm">Unable to load flow data</p>
        </div>
      </div>
    );
  }

  if (!dataWithColors.nodes || !dataWithColors.links || dataWithColors.nodes.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-text-muted text-sm">
        No flow data available
      </div>
    );
  }

  return (
    <div
      id="sankey-container"
      style={{ height: `${calculatedHeight}px`, width: '100%' }}
      onMouseMove={(e) => {
        const target = e.target;
        const container = e.currentTarget;

        // Nodes are <rect> elements
        if (target.tagName === 'rect' && target.hasAttribute('x') && target.hasAttribute('y')) {
          const width = parseFloat(target.getAttribute('width') || '0');
          if (width > 5) {
            const inert = isInertNodeRect(
              parseFloat(target.getAttribute('x')),
              parseFloat(target.getAttribute('y')),
              inertNodeBoxes.current
            );
            container.style.cursor = inert ? 'default' : 'pointer';
            return;
          }
        }

        // Links are <path> elements
        if (target.tagName === 'path' && target.hasAttribute('d')) {
          container.style.cursor = 'pointer';
          return;
        }

        container.style.cursor = 'default';
      }}
    >
      <ResponsiveSankey
        data={dataWithColors}
        margin={sankeyMargin}
        align="justify"
        sort={compareNodesByCategory}
        colors={getNodeColor}
        nodeOpacity={1}
        nodeHoverOthersOpacity={0.35}
        nodeThickness={18}
        nodeSpacing={nodeSpacing}
        nodeBorderWidth={0}
        nodeBorderColor={nodeBorderColor}
        linkOpacity={0.4}
        linkHoverOpacity={0.8}
        linkHoverOthersOpacity={0.1}
        linkContract={3}
        enableLinkGradient={true}
        linkBlendMode={isDark ? 'lighten' : 'normal'}
        onClick={handleSankeyClick}
        isInteractive={true}
        enableLabels={false}
        animate={false}
        layers={['links', 'nodes', CustomLabelsLayer, 'legends']}
        theme={sankeyTheme}
        nodeTooltip={({ node }) => {
          const IconComponent = TYPE_ICON_COMPONENTS[node.nodeType];
          return (
            <div style={{
              background: 'var(--color-panel-secondary)',
              padding: '10px 14px',
              borderRadius: '4px',
              border: '1px solid var(--color-border-strong)',
              minWidth: '180px',
            }}>
              <div style={{
                color: 'var(--color-text-primary)',
                fontWeight: 'bold',
                marginBottom: '6px',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                {IconComponent && <IconComponent size={14} />}
                {node.displayText}
              </div>
              {node.type && (
                <div style={{ color: 'var(--color-text-muted)', fontSize: '10px', marginBottom: '6px' }}>
                  Type: {getNodeTypeLabel(node.type)}
                </div>
              )}
              {/* Says why the band does not lead anywhere, rather than leaving a
                  dead click unexplained. */}
              {node.is_remote && node.service_name && (
                <div style={{ color: 'var(--color-text-muted)', fontSize: '10px', marginBottom: '6px' }}>
                  Ran in {node.service_name}. Open that service for its traces.
                </div>
              )}
              {/* The model is no longer part of the node's identity, so an agent
                  that routes across several shows them here instead of splitting
                  into one spine node per model. */}
              {node.model && (
                <div style={{ color: 'var(--color-text-secondary)', fontSize: '11px', marginBottom: '2px' }}>
                  Model: {node.model}
                  {node.models?.length > 1 && ` +${node.models.length - 1} more`}
                </div>
              )}
              {node.models?.length > 1 && (
                <div style={{ color: 'var(--color-text-supporting)', fontSize: '10px', marginBottom: '6px' }}>
                  {node.models.map(m => `${m.name} (${m.calls.toLocaleString()})`).join(', ')}
                </div>
              )}
              {node.trace_count !== undefined && (
                <div style={{ color: 'var(--color-text-secondary)', fontSize: '11px' }}>
                  Traces: {node.trace_count.toLocaleString()}
                </div>
              )}
              {/* Calls only differ from traces on a leaf call: one trace can
                  call the same tool many times. */}
              {node.calls !== undefined && node.calls !== node.trace_count && (
                <div style={{ color: 'var(--color-text-supporting)', fontSize: '11px' }}>
                  Calls: {node.calls.toLocaleString()}
                </div>
              )}
              {/* The tool repeats once per calling agent, so say how many
                  share it rather than drawing ribbons that converge. */}
              {node.callers > 1 && (
                <div style={{ color: 'var(--color-text-supporting)', fontSize: '11px' }}>
                  Called by {node.callers} agents
                </div>
              )}
              {node.loop_count > 0 && (
                <div style={{ color: 'var(--color-text-supporting)', fontSize: '11px' }}>
                  Returned to: {node.loop_count.toLocaleString()} {node.loop_count === 1 ? 'time' : 'times'}
                </div>
              )}
            </div>
          );
        }}
        linkTooltip={({ link }) => (
          <div style={{
            background: 'var(--color-panel-secondary)',
            padding: '8px 12px',
            borderRadius: '4px',
            border: '1px solid var(--color-border-strong)',
          }}>
            <div style={{ color: 'var(--color-text-primary)', fontWeight: 'bold', marginBottom: '4px', fontSize: '13px' }}>
              {link.source.displayText} → {link.target.displayText}
            </div>
            <div style={{ color: 'var(--color-text-secondary)', fontSize: '11px' }}>
              Traces: {link.value.toLocaleString()}
            </div>
            {/* Only a leaf call can diverge: one trace may call a tool many times. */}
            {link.calls !== undefined && link.calls !== link.value && (
              <div style={{ color: 'var(--color-text-supporting)', fontSize: '11px' }}>
                Calls: {link.calls.toLocaleString()}
              </div>
            )}
          </div>
        )}
    />

      {/* Slide-out drawer for node details */}
      <SlideOutDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedNode ? `${getNodeTypeLabel(selectedNode.nodeType)}: ${selectedNode.displayText}` : 'Node Details'}
        width="500px"
      >
        <DrawerContent
          node={selectedNode}
          selectedAppId={selectedAppId}
          selectedEnvironment={selectedEnvironment}
          selectedRange={selectedRange}
          dataCache={dataCache}
          setDataCache={setDataCache}
          toolDefsStatus={toolDefsStatus}
        />
      </SlideOutDrawer>
    </div>
  );
}

/**
 * Drawer content component that fetches data on demand and caches it
 */
function DrawerContent({ node, selectedAppId, selectedEnvironment, selectedRange, dataCache, setDataCache, toolDefsStatus }) {
  const { sessionId, getJwt } = useSessionJwt();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track which tool nodes we've already processed (prevents watcher effect from re-running on unrelated cache changes)
  const processedToolsRef = useRef(new Set());

  // Helper: Filter tool versions for a specific tool
  // Matched on filter_value, the bare name the service logged. displayText
  // carries the service for a node from another one, which matches no definition.
  // The cache key stays on displayText, which is what makes two services' tools
  // of the same name distinct entries.
  const getToolVersions = useCallback((allDefinitions, node) => {
    const toolName = node.filter_value || node.displayText;
    return allDefinitions.filter(d => d.tool_name === toolName);
  }, []);

  // Helper: Cache tool versions for a specific node
  const cacheToolVersions = useCallback((toolName, versions) => {
    const cacheKey = `TOOL-${toolName}`;
    setDataCache(prev => ({
      ...prev,
      [cacheKey]: { tool_versions: versions }
    }));
  }, [setDataCache]);

  useEffect(() => {
    const jwt = getJwt();
    if (!node || !jwt || !selectedAppId || !selectedEnvironment || !selectedRange) return;

    const cacheKey = `${node.nodeType}-${node.displayText}`;

    // Check cache first
    if (dataCache[cacheKey]) {
      return;
    }

    let controller; // Shared AbortController for cleanup

    // Fetch data based on node type
    if (node.nodeType === 'AGENT') {
      if (!node.agent_id) {
        setDataCache(prev => ({
          ...prev,
          [cacheKey]: { system_prompt: 'Agent ID not available - agent may not exist in agents table.' }
        }));
        return;
      }

      setLoading(true);
      setError(null);

      // Use the node's own service_id and environment so external/cross-service
      // agents are fetched from their owning service, not the selected app.
      const ownerServiceId = node.service_id || selectedAppId;
      const ownerEnvironment = node.environment || selectedEnvironment;
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const endpoint = v2ServicePath(ownerServiceId, `/agents/${node.agent_id}`);
      const url = `${apiUrl}${endpoint}?env=${encodeURIComponent(ownerEnvironment)}`;

      controller = new AbortController();

      fetch(url, {
        headers: { Authorization: `Bearer ${jwt}` },
        signal: controller.signal,
      })
        .then(res => {
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          const systemPrompt = data.data?.system_prompt || data.data?.system_prompt_preview || 'No system prompt available for this agent.';

          setDataCache(prev => ({
            ...prev,
            [cacheKey]: { system_prompt: systemPrompt }
          }));
          setLoading(false);
        })
        .catch(err => {
          if (err.name === 'AbortError') return;
          setError(err.message);
          setLoading(false);
        });
    } else if (node.nodeType === 'TOOL') {
      // Check prefetch status first
      if (toolDefsStatus.state === 'error') {
        // Background prefetch failed - show error immediately
        setError(toolDefsStatus.error);
        return;
      }

      if (toolDefsStatus.state === 'success') {
        // Background prefetch succeeded - use cache
        const allDefs = dataCache['__all_tool_definitions__'];
        if (!allDefs) {
          setError('Tool definitions not found in cache');
          return;
        }
        const versions = getToolVersions(allDefs, node);
        cacheToolVersions(node.displayText, versions);
        return;
      }

      if (toolDefsStatus.state === 'loading') {
        // Background prefetch in progress - show loading
        setLoading(true);
        setError(null);
        // Wait for prefetch to complete - watcher effect will handle it
        return;
      }

      // toolDefsStatus.state === 'idle'
      // Background prefetch hasn't started - fetch immediately (fallback)
      setLoading(true);
      setError(null);

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const endpoint = v2ServicePath(selectedAppId, '/tools/definitions');
      const url = `${apiUrl}${endpoint}?env=${encodeURIComponent(selectedEnvironment)}&range=${encodeURIComponent(selectedRange)}`;

      controller = new AbortController();

      fetch(url, {
        headers: { Authorization: `Bearer ${jwt}` },
        signal: controller.signal,
      })
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then(data => {
          const allDefinitions = data.data || [];
          const versions = getToolVersions(allDefinitions, node);

          setDataCache(prev => ({
            ...prev,
            '__all_tool_definitions__': allDefinitions,
          }));
          cacheToolVersions(node.displayText, versions);
          setLoading(false);
        })
        .catch(err => {
          if (err.name === 'AbortError') return;
          setError(err.message);
          setLoading(false);
        });
    }

    // Cleanup function that aborts any pending fetch
    return () => {
      if (controller) {
        controller.abort();
      }
    };
  }, [node, selectedAppId, selectedEnvironment, selectedRange, sessionId, getJwt, setDataCache, toolDefsStatus, getToolVersions, cacheToolVersions, setLoading, setError]);

  // Watch for prefetch completion while drawer is open for TOOL nodes
  useEffect(() => {
    if (!node || node.nodeType !== 'TOOL') return;
    if (toolDefsStatus.state !== 'success' && toolDefsStatus.state !== 'error') return;

    const cacheKey = `${node.nodeType}-${node.displayText}`;

    // Check if we've already processed this tool (using ref instead of cache to avoid re-runs)
    if (processedToolsRef.current.has(cacheKey)) return;

    if (toolDefsStatus.state === 'success') {
      const allDefs = dataCache['__all_tool_definitions__'];
      if (!allDefs) {
        setError('Tool definitions not found in cache');
        setLoading(false);
        return;
      }
      const versions = getToolVersions(allDefs, node);
      cacheToolVersions(node.displayText, versions);
      processedToolsRef.current.add(cacheKey); // Mark as processed
      setLoading(false);
    } else if (toolDefsStatus.state === 'error') {
      setError(toolDefsStatus.error);
      processedToolsRef.current.add(cacheKey); // Mark as processed (even on error)
      setLoading(false);
    }
  }, [toolDefsStatus.state, toolDefsStatus.error, node, dataCache, getToolVersions, cacheToolVersions, setLoading, setError]);

  if (!node) return null;

  const cacheKey = `${node.nodeType}-${node.displayText}`;
  const cachedData = dataCache[cacheKey];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-text-muted text-sm">
        Fetching data...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-danger/10 border border-danger/20 rounded-lg p-4">
        <p className="text-danger text-sm">Unable to load data</p>
      </div>
    );
  }

  if (node.nodeType === 'AGENT') {
    return (
      <SystemPromptPanel
        systemPrompt={cachedData?.system_prompt || 'No data available'}
      />
    );
  }

  if (node.nodeType === 'TOOL') {
    let displayContent = 'No data available';

    if (cachedData?.tool_versions && cachedData.tool_versions.length > 0) {
      try {
        const versions = cachedData.tool_versions;
        const versionTexts = [];

        for (let i = 0; i < versions.length; i++) {
          const version = versions[i];
          const toolDef = JSON.parse(version.tool_definition);
          const description = toolDef.description || 'No description provided';
          const parameters = toolDef.parameters_json_schema
            ? JSON.stringify(toolDef.parameters_json_schema, null, 2)
            : 'No parameter schema available';

          const timestamp = new Date(version.last_seen).toLocaleString();
          const versionLabel = i === 0 ? `LATEST - ${timestamp}` : timestamp;

          const separator = '='.repeat(80);
          const versionText = `[${versionLabel}]\n\n${description}\n\n${separator}\nPARAMETERS SCHEMA\n${separator}\n\n${parameters}`;
          versionTexts.push(versionText);
        }

        const versionSeparator = '\n\n' + '#'.repeat(80) + '\nVERSION SEPARATOR\n' + '#'.repeat(80) + '\n\n';
        displayContent = versionTexts.join(versionSeparator);
      } catch (e) {
        displayContent = 'No data available';
      }
    } else if (cachedData?.tool_versions && cachedData.tool_versions.length === 0) {
      displayContent = 'No data available';
    }

    return (
      <SystemPromptPanel
        title="Tool Definition"
        systemPrompt={displayContent}
      />
    );
  }

  return null;
}
